import { useEffect, useState } from 'react';
import { PqrListActions } from './actions';
import {
    GridColDef,
    GridPaginationModel,
    GridRowSelectionModel,
} from '@mui/x-data-grid';
import { asyncGetPqrs } from '../../../../../services/pqr.service';
import { ISelect } from '../../../../../shared/types/shared';
import { Pqr, PqrFilter } from '../../../../../shared/types/modules/pqr';
import { TableTooltip } from '../../../../components/general/table';
import { DateTooltip } from '../../../../components/general/dateTooltip';
import { useNavigate } from 'react-router-dom';
import { isOnlyViewProfile } from '../../../../../shared/types/profiles';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../redux/store';
import { Alerts } from '../../../../../shared/types/legalProceedings/alerts';

export const usePqrList = (
    filters?: PqrFilter,
    onSelectionModel?: (selectionModel: GridRowSelectionModel) => void
) => {
    const navigate = useNavigate();
    const { logUser } = useSelector((state: RootState) => state.general);
    const { type } = logUser || {};
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<Pqr[]>([]);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [total, setTotal] = useState<number>(0);
    const [finalFilters, setFinalFilters] = useState<PqrFilter>({});

    const paginator = (model: GridPaginationModel) => {
        if (model.page !== page) {
            setPage(model.page);
        } else if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
        }
    };

    const handleSelectionChange = (selectionModel: GridRowSelectionModel) => {
        if (onSelectionModel) onSelectionModel(selectionModel);
    };

    const columns: GridColDef<Pqr>[] = [];

    if (!isOnlyViewProfile(type) || type === 'cliente') {
        columns.push({
            field: 'actions',
            headerName: 'Acciones',
            width: 150,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => (
                <PqrListActions id={row.id} isSurvey={!!row?.row?.survey} />
            ),
        });
    }

    columns.push(
        {
            field: 'caseId',
            headerName: 'ID Caso',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <TableTooltip value={row.row.callInfo?.callId || ''} />
            ),
        },
        {
            field: 'consecutiveFormat',
            headerName: 'PQR ID',
            minWidth: 120,
            flex: 0.4,
        },
        {
            field: 'document',
            headerName: 'Cuenta',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <TableTooltip value={row.row.account?.document || ''} />
            ),
        },
        {
            field: 'name',
            headerName: 'Nombre de Cuenta',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => {
                const sanitize = (v?: string) => {
                    const s = (v || '').trim();
                    return s.toUpperCase() === 'NULL' ? '' : s;
                };
                const fullName = `${sanitize(row.row.account?.name)} ${sanitize(
                    row.row.account?.lastName
                )}`
                    .trim()
                    .replace(/\s+/g, ' ');

                return <TableTooltip value={fullName} />;
            },
        },
        {
            field: 'alert',
            headerName: 'ID Alerta',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip
                    value={
                        (row.row?.alert as Alerts)?.ruleNumber?.toString() || ''
                    }
                />
            ),
        },
        {
            field: 'half',
            headerName: 'Medio',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip
                    value={(row.row.data?.half as ISelect)?.name || ''}
                />
            ),
        },
        {
            field: 'product',
            headerName: 'Producto',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip
                    value={
                        (row.row.initialCausality?.product as ISelect)?.name ||
                        ''
                    }
                />
            ),
        },
        {
            field: 'reason',
            headerName: 'Motivo',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip
                    value={(row.row.initialCausality?.motive as ISelect)?.name}
                />
            ),
        },
        {
            field: 'created_at',
            headerName: 'Fecha de creación',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => <DateTooltip date={row.row.created_at} />,
        },
        {
            field: 'sacDate',
            headerName: 'Fecha SAC',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) =>
                row.row.statusManagement?.sacDate && (
                    <span style={{ color: row.row.colorSacDate }}>
                        <DateTooltip
                            date={row.row.statusManagement?.sacDate}
                            substractHours={0}
                        />
                    </span>
                ),
        },
        {
            field: 'state',
            headerName: 'Estado',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip
                    value={(row.row.info?.status as ISelect)?.name || ''}
                />
            ),
        },
        {
            field: 'furagDate',
            headerName: 'Fecha Normativa',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) =>
                row.row.statusManagement?.furagDate && (
                    <span style={{ color: row.row.colorFuragDate }}>
                        <DateTooltip
                            date={row.row.statusManagement?.furagDate}
                            substractHours={0}
                        />
                    </span>
                ),
        },
        {
            field: 'receptionChannel',
            headerName: 'Canal de recepción',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <TableTooltip
                    value={
                        (row.row.data?.receptionChannel as ISelect)?.name || ''
                    }
                />
            ),
        },
        {
            field: 'survey',
            headerName: '¿Se diligenció encuesta?',
            minWidth: 120,
            flex: 0.4,
            align: 'center',
            renderCell: (row) => <>{row.row.survey ? 'Si' : 'No'}</>,
        }
    );

    useEffect(() => {
        setPage(0);
        setFinalFilters(filters || {});
    }, [filters]);

    useEffect(() => {
        setLoading(true);
        asyncGetPqrs(finalFilters, page, pageSize)
            .then(async (res) => {
                setRows(res.items);
                setTotal(res.length);
            })
            .finally(() => setLoading(false));
    }, [page, pageSize, finalFilters]);

    return {
        navigate,
        columns,
        rows,
        page,
        pageSize,
        paginator,
        loading,
        total,
        handleSelectionChange,
    };
};
